import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  FilterChips,
  Modal,
  Pagination,
  Pill,
  TextareaField,
  fmtBRL,
  type Column,
} from "@atlas/ui/web";
import type { AssuntoAlteracaoConsignacao, PedidoDaConsignataria, TipoSolicitacaoConsignacao } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { useFiltros } from "../../lib/use-filtros";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../lib/banco-perms";
import { EncerramentoModal } from "./beneficios/encerrar-modal";
import { AlterarValorBeneficioModal } from "./beneficios/alterar-valor-modal";

// AS SOLICITAÇÕES DOS SERVIDORES (#318, epic #312): cancelamento e alteração.
//
// O servidor pede pelo app ou pela web; quem responde é a consignatária dona da
// rubrica — esta fila. As respostas dependem do pedido:
//   - cancelamento: encerrar o benefício e atender (com a prévia do que libera
//     de margem, a mesma da #317), ou recusar explicando por quê;
//   - alteração: alterar o valor e atender (a prévia da #320), atender sem
//     mexer no valor (ex.: dependente incluído na mesma mensalidade), ou recusar.
// Toda resposta chega ao servidor por e-mail e fica no histórico dele.
// Responder exige a permissão `solicitacoes`; quem só consulta vê a fila.

const POR_PAGINA = 25;
type Status = PedidoDaConsignataria["status"];
type FiltroTipo = TipoSolicitacaoConsignacao | "todos";

const reais = (centavos: number) => fmtBRL(centavos / 100);
const dataBr = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

const SITUACAO: Record<Status, { rotulo: string; variante: "pendente" | "averbado" | "expirado" }> = {
  aberta: { rotulo: "Aberto", variante: "pendente" },
  atendida: { rotulo: "Atendido", variante: "averbado" },
  recusada: { rotulo: "Recusado", variante: "expirado" },
};

export const ROTULO_ASSUNTO: Record<AssuntoAlteracaoConsignacao, string> = {
  mudar_plano: "Mudar de plano",
  dependentes: "Incluir ou excluir dependente",
  outro: "Outro assunto",
};

const quem = (p: PedidoDaConsignataria) => p.servidor.nome ?? `matrícula ${p.servidor.matricula}`;

export function BancoPedidosCancelamento() {
  const [status, setStatus] = useState<Status>("aberta");
  const [tipo, setTipo] = useState<FiltroTipo>("todos");
  const [pagina, setPagina] = useState(1);
  const [encerrando, setEncerrando] = useState<PedidoDaConsignataria | null>(null);
  const [alterando, setAlterando] = useState<PedidoDaConsignataria | null>(null);
  const [atendendo, setAtendendo] = useState<PedidoDaConsignataria | null>(null);
  const [recusando, setRecusando] = useState<PedidoDaConsignataria | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const podeResponder = bancoPermissaoCobre(readBancoPermissoesDaSessao(), "solicitacoes");

  // "Abertos" é o padrão: limpar volta para eles, não para "todos".
  const { filtrar } = useFiltros(
    [
      { nome: "status", ligado: status !== "aberta", limpar: () => setStatus("aberta") },
      { nome: "tipo", ligado: tipo !== "todos", limpar: () => setTipo("todos") },
    ],
    setPagina,
  );

  const q = useQuery({
    queryKey: ["banco", "pedidos-cancelamento", status, tipo, pagina],
    queryFn: () =>
      atlas.banco.pedidosCancelamento.listar({
        status,
        tipo: tipo === "todos" ? undefined : tipo,
        limit: POR_PAGINA,
        offset: (pagina - 1) * POR_PAGINA,
      }),
    placeholderData: keepPreviousData,
  });

  const colunas: Column<PedidoDaConsignataria>[] = [
    {
      key: "pedido",
      header: "Pedido",
      wrap: true,
      render: (p) => (
        <div style={{ display: "grid" }}>
          <span style={{ fontWeight: 600 }}>{p.tipo === "alteracao" ? "Alteração" : "Cancelamento"}</span>
          {p.assunto ? <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{ROTULO_ASSUNTO[p.assunto]}</span> : null}
        </div>
      ),
    },
    {
      key: "servidor",
      header: "Servidor",
      wrap: true,
      render: (p) => (
        <div style={{ display: "grid" }}>
          <span>{p.servidor.nome ?? "—"}</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            mat. {p.servidor.matricula} · {p.servidor.cpfMasked}
          </span>
        </div>
      ),
    },
    { key: "orgao", header: "Órgão/entidade", wrap: true, render: (p) => p.orgao },
    {
      key: "beneficio",
      header: "Benefício",
      wrap: true,
      render: (p) => (
        <Link to={`/banco/beneficios/${encodeURIComponent(p.beneficio.id)}`}>
          {p.beneficio.rotulo} · rubrica {p.beneficio.rubrica}
        </Link>
      ),
    },
    { key: "valor", header: "Valor mensal", align: "right", render: (p) => (p.valorVigenteCentavos == null ? "—" : reais(p.valorVigenteCentavos)) },
    { key: "pedidoEm", header: "Pedido em", render: (p) => dataBr(p.solicitadaEm) },
    {
      key: "responderAte",
      header: "Responder até",
      render: (p) => (
        <span style={{ color: p.vencido ? "var(--danger-500)" : "var(--text)", fontWeight: p.vencido ? 600 : 400 }}>
          {dataBr(p.responderAte)}
          {p.vencido ? " · vencido" : ""}
        </span>
      ),
    },
    {
      key: "situacao",
      header: "Situação",
      render: (p) => <Pill variant={SITUACAO[p.status].variante}>{SITUACAO[p.status].rotulo}</Pill>,
    },
    {
      key: "motivo",
      header: status === "aberta" ? "O que o servidor escreveu" : "Resposta",
      wrap: true,
      render: (p) => {
        if (status === "aberta") return p.motivo ?? <span style={{ color: "var(--text-dim)" }}>não informado</span>;
        const por = p.respondidaPor?.autor === "averbadora" ? " (pela averbadora)" : "";
        return `${p.resposta ?? "—"}${por}`;
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Solicitações</h1>
        <div style={{ color: "var(--text-muted)", maxWidth: 720 }}>
          Os servidores pedem pelo app ou pela web o cancelamento ou a alteração dos benefícios que você administra.
          Responda dentro do prazo do órgão — o servidor recebe a resposta por e-mail e a vê no histórico dele.
        </div>
      </header>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <FilterChips
          label="Situação"
          variant="segmentado"
          value={status}
          onChange={filtrar((v: string) => setStatus(v as Status))}
          options={[
            { value: "aberta", label: "Abertos" },
            { value: "atendida", label: "Atendidos" },
            { value: "recusada", label: "Recusados" },
          ]}
        />
        <FilterChips
          label="Tipo"
          variant="segmentado"
          value={tipo}
          onChange={filtrar((v: string) => setTipo(v as FiltroTipo))}
          options={[
            { value: "todos", label: "Todos" },
            { value: "cancelamento", label: "Cancelamento" },
            { value: "alteracao", label: "Alteração" },
          ]}
        />
      </div>

      {aviso ? (
        <Card>
          <p role="status" style={{ margin: 0 }}>{aviso}</p>
        </Card>
      ) : null}

      <Card>
        {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar as solicitações.")}</p>}
        <DataTable
          caption="Solicitações dos servidores"
          columns={colunas}
          rows={q.data?.itens ?? []}
          rowKey={(p) => String(p.id)}
          loading={q.isLoading}
          emptyState={
            <EstadoVazio
              titulo={status === "aberta" ? "Nenhuma solicitação esperando resposta" : "Nenhuma solicitação nesta situação"}
            />
          }
          actions={
            podeResponder && status === "aberta"
              ? (p) => (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {p.tipo === "alteracao" ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setAlterando(p)}>Alterar valor e atender</Button>
                        <Button size="sm" variant="ghost" onClick={() => setAtendendo(p)}>Atender sem mudar valor</Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setEncerrando(p)}>Encerrar e atender</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setRecusando(p)}>Recusar</Button>
                  </div>
                )
              : undefined
          }
        />
        <Pagination
          total={q.data?.total ?? 0}
          pagina={pagina}
          porPagina={POR_PAGINA}
          onPagina={setPagina}
          onPorPagina={() => undefined}
          rotulo="solicitações"
        />
      </Card>

      {encerrando ? (
        <EncerramentoModal
          chave={`pedido:${encerrando.id}`}
          titulo="Encerrar e atender o pedido"
          rotuloConfirmar="Encerrar e atender"
          descricao={`${encerrando.beneficio.rotulo} de ${quem(encerrando)} — ${encerrando.orgao}. O pedido do servidor será marcado como atendido.`}
          carregarPrevia={(fim) => atlas.banco.pedidosCancelamento.previa(encerrando.id, fim)}
          confirmar={(competenciaFim, motivo) =>
            atlas.banco.pedidosCancelamento.responder(encerrando.id, { acao: "encerrar_e_atender", competenciaFim, motivo })
          }
          onFechar={() => setEncerrando(null)}
          onFeito={setAviso}
        />
      ) : null}
      {alterando ? (
        <AlterarValorBeneficioModal
          beneficio={{
            id: alterando.beneficio.id,
            rotulo: alterando.beneficio.rotulo,
            valorCentavos: alterando.valorVigenteCentavos ?? 0,
            nome: alterando.servidor.nome,
            matricula: alterando.servidor.matricula,
            orgao: alterando.orgao,
          }}
          titulo="Alterar valor e atender o pedido"
          rotuloConfirmar="Alterar e atender"
          aviso={`O servidor pediu: ${alterando.assunto ? `${ROTULO_ASSUNTO[alterando.assunto]} — ` : ""}${alterando.motivo ?? "sem descrição"}`}
          carregarPrevia={(q) => atlas.banco.pedidosCancelamento.previaAlteracao(alterando.id, q)}
          confirmar={(b) => atlas.banco.pedidosCancelamento.responder(alterando.id, { acao: "alterar_e_atender", ...b })}
          onFechar={() => setAlterando(null)}
          onFeito={(a) => setAviso(`${a} O pedido do servidor foi atendido.`)}
        />
      ) : null}
      {atendendo ? (
        <ResponderModal pedido={atendendo} acao="atender" onFechar={() => setAtendendo(null)} onFeito={setAviso} />
      ) : null}
      {recusando ? (
        <ResponderModal pedido={recusando} acao="recusar" onFechar={() => setRecusando(null)} onFeito={setAviso} />
      ) : null}
    </div>
  );
}

/** Recusar (qualquer pedido) ou atender sem mexer no valor (alteração): só o texto da resposta. */
function ResponderModal({
  pedido,
  acao,
  onFechar,
  onFeito,
}: {
  pedido: PedidoDaConsignataria;
  acao: "recusar" | "atender";
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  // A mesma régua da averbadora: resposta sem explicação não diz ao servidor o que fazer.
  const valido = motivo.trim().length >= 10;
  const salvar = useMutation({
    mutationFn: () => atlas.banco.pedidosCancelamento.responder(pedido.id, { acao, motivo: motivo.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["banco", "pedidos-cancelamento"] });
      void qc.invalidateQueries({ queryKey: ["banco", "beneficios"] });
      void qc.invalidateQueries({ queryKey: ["banco", "operadora"] });
      onFeito(
        acao === "recusar"
          ? "Pedido recusado. O servidor recebe a resposta por e-mail e a vê no histórico."
          : "Pedido atendido. O servidor recebe a resposta por e-mail e a vê no histórico.",
      );
      onFechar();
    },
  });
  return (
    <Modal
      open
      title={acao === "recusar" ? "Recusar o pedido" : "Atender o pedido sem mudar o valor"}
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Enviando…" : acao === "recusar" ? "Recusar" : "Atender"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ margin: 0 }}>
          {pedido.beneficio.rotulo} de {quem(pedido)} — {pedido.orgao}.{" "}
          {acao === "recusar" ? "O desconto continua como está." : "O valor do desconto não muda."}
        </p>
        {pedido.motivo ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            O servidor pediu: {pedido.assunto ? `${ROTULO_ASSUNTO[pedido.assunto]} — ` : ""}
            {pedido.motivo}
          </p>
        ) : null}
        <TextareaField
          label={acao === "recusar" ? "Motivo da recusa" : "O que foi feito"}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          hint="O servidor lê esta resposta. Pelo menos 10 caracteres."
        />
        {salvar.isError && (
          <p role="alert">{mensagemDeErro(salvar.error, acao === "recusar" ? "Não foi possível recusar." : "Não foi possível atender.")}</p>
        )}
      </div>
    </Modal>
  );
}
